import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Layers, Pencil, Trash2 } from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie, getSessionMainId } from "../utils/getCookie";
import Loader from "../Loader";
import "./AddAdmission.css";
import Swal from "sweetalert2";

export default function AdmissionBatches() {
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [staffMap, setStaffMap] = useState({});
    const [load, setLoad] = useState(true);

    const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const loadBatches = async () => {
        setLoad(true);
        try {
            const mainId = getSessionMainId();
            const staffQs = mainId ? `?main_id=${encodeURIComponent(mainId)}` : "";
            const [bRes, sRes] = await Promise.all([
                fetch(`${url}/admission/batches`, { headers }),
                fetch(`${url}/staff${staffQs}`, { headers }),
            ]);
            const bJson = await bRes.json();
            const staff = sRes.ok ? await sRes.json() : [];
            const map = {};
            (Array.isArray(staff) ? staff : []).forEach((s) => {
                const id = s.emplyee_ID != null ? String(s.emplyee_ID) : "";
                if (id)
                    map[id] = [s.firstName, s.lastName].filter(Boolean).join(" ").trim() || id;
            });
            setStaffMap(map);
            setRows(Array.isArray(bJson?.batches) ? bJson.batches : []);
        } catch (e) {
            console.error(e);
            setRows([]);
        } finally {
            setLoad(false);
        }
    };

    useEffect(() => {
        loadBatches();
    }, []);

    const teacherLabel = (tid) => {
        if (!tid) return "—";
        return staffMap[String(tid)] || `ID ${tid}`;
    };

    const onDelete = async (row) => {
        const r = await Swal.fire({
            title: "Delete batch?",
            text: row.studentCount > 0 ? "Students are still assigned — delete is blocked." : "This cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
        });
        if (!r.isConfirmed) return;
        try {
            const res = await fetch(`${url}/admission/batches/${row.id}`, {
                method: "DELETE",
                headers,
            });
            const j = await res.json();
            if (!res.ok) {
                Swal.fire("Error", j.message || "Delete failed", "error");
                return;
            }
            Swal.fire("Deleted", "", "success");
            loadBatches();
        } catch (e) {
            Swal.fire("Error", e.message, "error");
        }
    };

    const isLoggedIn = getDecryptedUserFromCookie();

    return (
        <div className="admission-form-shell">
            <div className="admission-form-shell__bg" aria-hidden />
            <div className="admission-form-container">
                <header className="admission-form-header">
                    <button
                        type="button"
                        className="admission-form-back"
                        onClick={() => navigate("/admission")}
                    >
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Admissions
                    </button>
                    <div className="admission-form-header-text">
                        <h1>
                            <Layers
                                size={22}
                                style={{ display: "inline", verticalAlign: "middle", marginRight: 8 }}
                                aria-hidden
                            />
                            Batches
                        </h1>
                        <p>
                            Schedule, teacher, and capacity. Students link via <code>batch_id</code> only — no
                            duplicated timings on the student row.
                        </p>
                    </div>
                    {isLoggedIn?.main_id && (
                        <Link to="/admission-batches/new" className="admission-form-btn admission-form-btn--primary">
                            New batch
                        </Link>
                    )}
                </header>

                <div className="admission-form-card" style={{ padding: "1.25rem" }}>
                    {load ? (
                        <Loader />
                    ) : rows.length === 0 ? (
                        <p className="admission-empty__hint">No batches yet. Create one to use on admissions.</p>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table className="admission-batches-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>
                                        <th style={{ padding: "0.5rem 0" }}>Name</th>
                                        <th>Center</th>
                                        <th>Teacher</th>
                                        <th>Schedule</th>
                                        <th>Days</th>
                                        <th>Students</th>
                                        <th>Status</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((b) => (
                                        <tr key={b.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                            <td style={{ padding: "0.65rem 0", fontWeight: 600 }}>{b.name}</td>
                                            <td>{b.center_id}</td>
                                            <td>{teacherLabel(b.teacher_id)}</td>
                                            <td>
                                                {b.start_time} – {b.end_time}
                                            </td>
                                            <td>
                                                {Array.isArray(b.days) && b.days.length
                                                    ? b.days.join(", ")
                                                    : "—"}
                                            </td>
                                            <td>
                                                {b.studentCount ?? 0}
                                                {b.max_students ? ` / ${b.max_students}` : ""}
                                            </td>
                                            <td>{b.status}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <button
                                                    type="button"
                                                    className="admission-form-btn admission-form-btn--secondary"
                                                    style={{ padding: "0.35rem 0.6rem", marginRight: 6 }}
                                                    onClick={() => navigate(`/admission-batches/${b.id}`)}
                                                >
                                                    <Pencil size={14} style={{ verticalAlign: "middle" }} aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="admission-form-btn admission-form-btn--secondary"
                                                    style={{ padding: "0.35rem 0.6rem" }}
                                                    onClick={() => onDelete(b)}
                                                >
                                                    <Trash2 size={14} style={{ verticalAlign: "middle" }} aria-hidden />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
